import * as React from "react"

import * as AccordionPrimitives from "@radix-ui/react-accordion"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronDown } from "lucide-react"

import { cn } from "../../cn"

/* ── CVA variants ─────────────────────────────────────────── */

const accordionItemVariants = cva(["border-b border-coval-border"])

const accordionTriggerVariants = cva([
  "flex flex-1 items-center justify-between py-3 text-sm font-medium cursor-pointer",
  "text-coval-text",
  "transition-colors duration-150",
  "not-disabled:hover:text-coval-text-muted",
  "disabled:cursor-default disabled:opacity-50",
  "[&[data-state=open]>svg]:rotate-180",
])

const accordionContentVariants = cva([
  "overflow-hidden text-sm text-coval-text",
  "data-[state=open]:animate-accordion-down",
  "data-[state=closed]:animate-accordion-up",
])

/* ── Accordion (root) ────────────────────────────────────── */

type AccordionProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitives.Root
>

// Roles that cannot carry an accessible name, so a label on them is lost.
const UNNAMEABLE_ROLES = new Set(["presentation", "none", "generic"])

/**
 * Radix renders the root as a plain `div`, where an `aria-label` names
 * nothing. A labelled accordion is exposed as a group so the label reaches
 * assistive tech. A caller's own `role` (`region`, say) is kept, unless it is
 * one that cannot be named while a label is set.
 */
const Accordion = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Root>,
  AccordionProps
>(({ role, ...props }, ref) => {
  const isLabelled =
    props["aria-label"] !== undefined || props["aria-labelledby"] !== undefined
  const resolvedRole =
    isLabelled && (role === undefined || UNNAMEABLE_ROLES.has(role))
      ? "group"
      : role
  return <AccordionPrimitives.Root ref={ref} {...props} role={resolvedRole} />
})

Accordion.displayName = "Accordion"

/* ── AccordionItem ───────────────────────────────────────── */

export interface AccordionItemProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitives.Item>,
    VariantProps<typeof accordionItemVariants> {}

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Item>,
  AccordionItemProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitives.Item
    ref={ref}
    className={cn(accordionItemVariants(), className)}
    {...props}
  />
))

AccordionItem.displayName = "AccordionItem"

/* ── AccordionTrigger ────────────────────────────────────── */

export interface AccordionTriggerProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitives.Trigger>,
    VariantProps<typeof accordionTriggerVariants> {
  // Suppress the built-in chevron when the caller renders its own indicator.
  hideChevron?: boolean
  // Class applied to the wrapping header (e.g. for sticky positioning).
  headerClassName?: string
}

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Trigger>,
  AccordionTriggerProps
>(
  (
    { className, children, hideChevron = false, headerClassName, ...props },
    ref,
  ) => (
    <AccordionPrimitives.Header className={cn("flex", headerClassName)}>
      <AccordionPrimitives.Trigger
        ref={ref}
        className={cn(accordionTriggerVariants(), className)}
        {...props}
      >
        {children}
        {!hideChevron && (
          <ChevronDown
            aria-hidden="true"
            className="h-3.5 w-3.5 shrink-0 text-coval-text-muted transition-transform duration-200"
          />
        )}
      </AccordionPrimitives.Trigger>
    </AccordionPrimitives.Header>
  ),
)

AccordionTrigger.displayName = "AccordionTrigger"

/* ── AccordionContent ────────────────────────────────────── */

export interface AccordionContentProps
  extends
    React.ComponentPropsWithoutRef<typeof AccordionPrimitives.Content>,
    VariantProps<typeof accordionContentVariants> {}

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitives.Content>,
  AccordionContentProps
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitives.Content
    ref={ref}
    className={cn(accordionContentVariants(), className)}
    {...props}
  >
    <div className="pb-3">{children}</div>
  </AccordionPrimitives.Content>
))

AccordionContent.displayName = "AccordionContent"

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  accordionItemVariants,
  accordionTriggerVariants,
  accordionContentVariants,
}
